import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { formatShorthandDollars } from "@common/components/utils";
import { TEXT_STYLE, X_AXIS_LABEL_KEY } from "./constants";
import { Tooltip, TooltipProps } from "./Tooltip";
import { useMemo, useState } from "react";
import { DataKey } from "./types";
import { ActiveBar } from "./ActiveBar";
import { ChartQuery, useChartQuery } from "./useChartQuery";
import { differenceInDays } from "date-fns";
import { GraphAnalyticInterval } from "@common/utils/graphql";
import { cn } from "@/utils/utils";
import { Loading } from "./Loading";
import { Error } from "./Error";
import { Empty } from "./Empty";

// Types
type Props = {
  query: Omit<ChartQuery, "interval">;
  className?: string;
};

// Component
export const Chart = ({ query, className }: Props) => {
  const interval = useMemo(() => {
    const diffInDays = differenceInDays(query.endDate, query.startDate);
    if (diffInDays < 30) {
      return GraphAnalyticInterval.Daily;
    } else if (diffInDays <= 60) {
      return GraphAnalyticInterval.Weekly;
    } else {
      return GraphAnalyticInterval.Monthly;
    }
  }, [query.endDate, query.startDate]);
  const isDaily = interval === GraphAnalyticInterval.Daily;
  const typedQuery = useMemo(
    () => ({ ...query, interval }) as ChartQuery,
    [query, interval],
  );
  const { config, data, loading } = useChartQuery(typedQuery);

  const [hoveredDataKey, setHoveredDataKey] = useState<DataKey | null>(null);
  const [showsActiveBar, setShowsActiveBar] = useState(false);
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  if (loading) {
    return <Loading className={className} />;
  } else if (!config || !data) {
    return <Error className={className} />;
  } else if (data.length === 0) {
    return <Empty className={className} />;
  }

  const dataKeys = Object.keys(config);

  return (
    // This div is needed, otherwise the chart overflows
    <div>
      <ChartContainer
        config={config}
        // recharts-tooltip-cursor: add fill to bar columns
        // recharts-surface: shows overflow of labels
        className={cn(
          "h-full w-full [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-white/10 [&_.recharts-surface]:overflow-visible",
          className,
        )}
      >
        <BarChart accessibilityLayer data={data}>
          <CartesianGrid
            vertical={false}
            stroke="#676767"
            strokeOpacity={0.3}
            strokeDasharray="4 4"
          />
          <ChartTooltip
            content={({ payload = [], label }) => {
              const data: TooltipProps["data"] = {};
              // 1. Filter out data keys that are somehow not in the config
              // 2. Sort the data keys in config order (which is how they are displayed in bars)
              // 3. Map the data to the tooltip (verify types)
              payload
                .filter((item) => dataKeys.includes(item.dataKey as DataKey))
                .sort(
                  (a, b) =>
                    dataKeys.indexOf(b.dataKey as DataKey) -
                    dataKeys.indexOf(a.dataKey as DataKey),
                )
                .forEach((item) => {
                  if (
                    typeof item.dataKey === "string" &&
                    typeof item.name === "string" &&
                    typeof item.value === "number" &&
                    typeof item.color === "string"
                  ) {
                    data[item.dataKey] = {
                      label: item.name,
                      value: item.value,
                      color: item.color,
                    };
                  }
                });
              return (
                <Tooltip
                  xAxisLabel={label}
                  data={data}
                  hoveredDataKey={hoveredDataKey}
                />
              );
            }}
          />
          <XAxis
            dataKey={X_AXIS_LABEL_KEY}
            tickLine={false}
            axisLine={false}
            style={TEXT_STYLE}
            // Hide the X-axis labels for daily data
            // But don't hide it completely so it still takes up space, use "" instead
            tickFormatter={(tick) => (isDaily ? "" : tick)}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickFormatter={(tick) => formatShorthandDollars(tick)}
            style={TEXT_STYLE}
          />
          {Object.entries(config).map(([dataKey, value], index) => {
            const isTopBar = index === dataKeys.length - 1;
            return (
              <Bar
                dataKey={dataKey}
                stackId="a"
                fill={value.color}
                isAnimationActive={false} // removes the bar animation
                onMouseEnter={() => {
                  setHoveredDataKey(dataKey);
                  setHoveredBarIndex(index);
                }}
                onMouseLeave={() => {
                  setHoveredDataKey(null);
                  setHoveredBarIndex(null);
                }}
                opacity={showsActiveBar ? 0.5 : 1}
                activeBar={(props: any) => (
                  <ActiveBar props={props} loadCallback={setShowsActiveBar} />
                )}
                key={index}
              >
                {/* TODO: hide this for daily data */}
                {isTopBar && (
                  <LabelList
                    position="top"
                    offset={8}
                    style={TEXT_STYLE}
                    valueAccessor={({ payload = [], xAxisLabel }: any) => {
                      // If daily data, only show the label if the bar is hovered
                      if (isDaily) {
                        if (!hoveredBarIndex) {
                          return "";
                        }
                        const hoveredBar = data[hoveredBarIndex];
                        if (hoveredBar.xAxisLabel !== xAxisLabel) {
                          return "";
                        }
                      }

                      let sum = 0;
                      // 1. Filter out the X_AXIS_LABEL_KEY
                      // 2. Sum the remaining values (should be numbers, but check just in case)
                      Object.entries(payload)
                        .filter(([dataKey]) => dataKey !== X_AXIS_LABEL_KEY)
                        .forEach(([dataKey, value]) => {
                          if (
                            dataKey !== X_AXIS_LABEL_KEY &&
                            typeof value === "number"
                          ) {
                            sum += value;
                          }
                        });
                      return formatShorthandDollars(sum);
                    }}
                  />
                )}
              </Bar>
            );
          })}
        </BarChart>
      </ChartContainer>
    </div>
  );
};
